/**
 * Social post links (YouTube, TikTok, Instagram, Reddit): pure host + path
 * parsing, no network. Shared so the api (`lib/social`, the brain's
 * force-URL-search gate) and the web (which must not send a social link down
 * the product-URL search path) agree on what a social post link is.
 * Short/share links (`vm.tiktok.com`, `/r/x/s/…`, `instagram.com/share/…`)
 * parse with `postKey: null` and `needsResolve: true`; the api follows them.
 */

export type SocialPlatform = 'youtube' | 'tiktok' | 'instagram' | 'reddit'

export interface ParsedSocialUrl {
  platform: SocialPlatform
  /** Native post id: YouTube video id, TikTok numeric id, Instagram shortcode, Reddit base36 id. Null until a short link is resolved. */
  postKey: string | null
  canonicalUrl: string
  /** True when the link is a short/share link that needs a redirect hop to get the post key. */
  needsResolve: boolean
}

const URL_IN_TEXT =
  /(?:https?:\/\/)?(?:[a-z0-9-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be|tiktok\.com|instagram\.com|reddit\.com|redd\.it)(?:\/[^\s<>"'`]*)?/gi

const TRAILING_PUNCTUATION = /[.,;:!?)\]}>*~]+$/

const YT_ID = /^[A-Za-z0-9_-]{11}$/
const IG_CODE = /^[A-Za-z0-9_-]{5,}$/
const REDDIT_ID = /^[a-z0-9]{3,10}$/i
const TIKTOK_ID = /^\d{8,25}$/

function hostOf(url: URL): string {
  return url.hostname.toLowerCase().replace(/^www\./, '')
}

function pathSegments(url: URL): string[] {
  return url.pathname.split('/').filter(Boolean)
}

function parseYouTube(url: URL, host: string): ParsedSocialUrl | null {
  let id: string | null = null
  if (host === 'youtu.be') {
    id = pathSegments(url)[0] ?? null
  } else if (
    host === 'youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'music.youtube.com' ||
    host === 'youtube-nocookie.com'
  ) {
    const [first, second] = pathSegments(url)
    if (first === 'watch') id = url.searchParams.get('v')
    else if (first && ['shorts', 'live', 'embed', 'v', 'e'].includes(first))
      id = second ?? null
  }
  if (!id || !YT_ID.test(id)) return null
  return {
    platform: 'youtube',
    postKey: id,
    canonicalUrl: `https://www.youtube.com/watch?v=${id}`,
    needsResolve: false,
  }
}

function parseTikTok(url: URL, host: string): ParsedSocialUrl | null {
  if (!(host === 'tiktok.com' || host.endsWith('.tiktok.com'))) return null
  const segs = pathSegments(url)

  if (host === 'vm.tiktok.com' || host === 'vt.tiktok.com') {
    if (!segs[0]) return null
    return {
      platform: 'tiktok',
      postKey: null,
      canonicalUrl: `https://${host}/${segs[0]}/`,
      needsResolve: true,
    }
  }
  if (segs[0] === 't' && segs[1]) {
    return {
      platform: 'tiktok',
      postKey: null,
      canonicalUrl: `https://www.tiktok.com/t/${segs[1]}/`,
      needsResolve: true,
    }
  }

  // /@user/video/<id>, /@user/photo/<id>, /v/<id>.html, /embed/v2/<id>
  let user: string | null = null
  let id: string | null = null
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i] ?? ''
    const next = segs[i + 1]
    if (seg.startsWith('@')) user = seg
    if ((seg === 'video' || seg === 'photo' || seg === 'v') && next) {
      id = next.replace(/\.html$/, '')
      break
    }
    if (seg === 'embed') {
      id = segs[segs.length - 1] ?? null
      break
    }
  }
  if (!id || !TIKTOK_ID.test(id)) return null
  return {
    platform: 'tiktok',
    postKey: id,
    canonicalUrl: user
      ? `https://www.tiktok.com/${user}/video/${id}`
      : `https://www.tiktok.com/@/video/${id}`,
    needsResolve: false,
  }
}

const IG_KINDS: Partial<Record<string, string>> = {
  reel: 'reel',
  reels: 'reel',
  p: 'p',
  tv: 'tv',
}

function igKind(seg: string | undefined): string | undefined {
  return seg ? IG_KINDS[seg] : undefined
}

function parseInstagram(url: URL, host: string): ParsedSocialUrl | null {
  if (host !== 'instagram.com' && host !== 'm.instagram.com') return null
  const segs = pathSegments(url)

  if (segs[0] === 'share' && segs.length >= 2) {
    const token = segs[segs.length - 1]
    const kind = segs.length >= 3 ? igKind(segs[1]) : undefined
    return {
      platform: 'instagram',
      postKey: null,
      canonicalUrl: `https://www.instagram.com/share/${kind ? `${segs[1]}/` : ''}${token}/`,
      needsResolve: true,
    }
  }

  // /reel/<code>, or with a username prefix: /<user>/reel/<code>
  const kindIndex = igKind(segs[0]) ? 0 : igKind(segs[1]) ? 1 : -1
  if (kindIndex < 0) return null
  const code = segs[kindIndex + 1]
  if (!code || !IG_CODE.test(code)) return null
  const kind = igKind(segs[kindIndex])
  return {
    platform: 'instagram',
    postKey: code,
    canonicalUrl: `https://www.instagram.com/${kind}/${code}/`,
    needsResolve: false,
  }
}

const REDDIT_HOSTS = new Set([
  'reddit.com',
  'old.reddit.com',
  'new.reddit.com',
  'np.reddit.com',
  'm.reddit.com',
  'i.reddit.com',
  'amp.reddit.com',
  'sh.reddit.com',
])

function parseReddit(url: URL, host: string): ParsedSocialUrl | null {
  const segs = pathSegments(url)

  if (host === 'redd.it') {
    const id = segs[0]
    if (!id || !REDDIT_ID.test(id)) return null
    const key = id.toLowerCase()
    return {
      platform: 'reddit',
      postKey: key,
      canonicalUrl: `https://www.reddit.com/comments/${key}/`,
      needsResolve: false,
    }
  }
  if (!REDDIT_HOSTS.has(host)) return null

  // /r/<sub>/s/<token> share link
  if (
    (segs[0] === 'r' || segs[0] === 'u' || segs[0] === 'user') &&
    segs[2] === 's' &&
    segs[3]
  ) {
    return {
      platform: 'reddit',
      postKey: null,
      canonicalUrl: `https://www.reddit.com/${segs[0]}/${segs[1]}/s/${segs[3]}`,
      needsResolve: true,
    }
  }

  // /r/<sub>/comments/<id>/<slug>, /user/<u>/comments/<id>/…, /comments/<id>
  const at = segs.indexOf('comments')
  if (at < 0) return null
  const id = segs[at + 1]
  if (!id || !REDDIT_ID.test(id)) return null
  const key = id.toLowerCase()
  const prefix = at >= 2 ? `${segs[at - 2]}/${segs[at - 1]}/` : ''
  const slug = segs[at + 2] ? `${segs[at + 2]}/` : ''
  return {
    platform: 'reddit',
    postKey: key,
    canonicalUrl: `https://www.reddit.com/${prefix}comments/${key}/${slug}`,
    needsResolve: false,
  }
}

function toUrl(raw: string): URL | null {
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    return url
  } catch {
    return null
  }
}

/** Parses one URL string. Canonical URLs are rebuilt from the post id, so tracking params never survive. */
export function parseSocialPostUrl(raw: string): ParsedSocialUrl | null {
  const url = toUrl(raw.trim())
  if (!url) return null
  const host = hostOf(url)
  return (
    parseYouTube(url, host) ??
    parseTikTok(url, host) ??
    parseInstagram(url, host) ??
    parseReddit(url, host)
  )
}

/** Finds the first supported social post link in free text. */
export function parseSocialUrl(text: string): ParsedSocialUrl | null {
  if (!text) return null
  for (const match of text.matchAll(URL_IN_TEXT)) {
    const start = match.index ?? 0
    // Skip matches glued to a preceding word char or dot (e.g. "notyoutube.com", "evil.com/youtube.com").
    const before = start > 0 ? text[start - 1] : ''
    if (before && /[\w.@/-]/.test(before)) continue
    const candidate = match[0].replace(TRAILING_PUNCTUATION, '')
    const parsed = parseSocialPostUrl(candidate)
    if (parsed) return parsed
  }
  return null
}

/** Pure host+path check: is this one URL a supported social post (or short/share link to one)? */
export function isSocialPostUrl(url: string): boolean {
  return parseSocialPostUrl(url) !== null
}
